<template>
  <div class="w-full">
    <!-- Alert de Rodada Iniciada -->
    <div v-if="isRoundAlreadyStarted && !hasFixturePostponed" class="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4 mb-4 shadow-sm">
      <div class="flex items-center justify-center">
        <i class="fas fa-info-circle text-blue-500 text-xl mr-3"></i>
        <div>
          <p class="font-semibold text-blue-900">Rodada em andamento</p>
          <p class="text-sm text-blue-700">Os palpites para esta rodada já foram iniciados</p>
        </div>
      </div>
    </div>

    <!-- Card Principal -->
    <div class="bg-white rounded-lg shadow-md overflow-hidden">
      <!-- Header -->
      <div class="px-6 py-4 border-b border-gray-200">
        <div class="flex flex-col gap-4">
          <!-- Título -->
          <div>
            <h2 class="text-2xl font-bold text-gray-900 mb-1">
              ⚽ {{ this.formatTitle(currentRound._id)}}
            </h2>
            <p class="text-sm text-gray-600">Faça seus palpites para esta rodada</p>
          </div>
          
          <!-- Ações em linha -->
          <div class="flex flex-col sm:flex-row gap-2 sm:items-center">
            <!-- Seletor de Rodada (Admin) -->
            <div v-show="hasPermission('SuperAdmin')" class="flex-1 sm:max-w-xs">
              <select 
                v-model="currentRound._id" 
                @change="onChangeSelect"
                class="block w-full px-3 py-2 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm">
                <option v-for="round in roundIds" :key="round.value" :value="round.value">
                  {{ round.label }}
                </option>
              </select>
            </div>

            <!-- Espaçador -->
            <div class="flex-1 hidden sm:block"></div>

            <!-- Botões de Ação -->
            <div class="flex gap-2 w-full sm:w-auto justify-between sm:justify-end">
              <button 
                v-show="hasPermission('UserBolao')"
                @click="shareBet" 
                :disabled="disableSubmit"
                class="flex items-center justify-center flex-1 sm:flex-none px-4 py-2 text-white bg-green-600 hover:bg-green-800 font-semibold text-sm rounded-lg transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                type="button">
                <i class="fab fa-whatsapp mr-2"></i> Compartilhar
              </button>
              
              <button 
                v-show="!isBetDisabled() || hasFixturePostponed"
                @click="submitForm"
                :disabled="disableSubmit"
                class="flex items-center justify-center flex-1 sm:flex-none px-6 py-2 text-white bg-green-600 hover:bg-green-800 font-bold text-sm rounded-lg transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                type="submit">
                <i class="fas fa-save mr-2"></i> Salvar Palpites
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Form -->
      <form id="app-form" @submit.prevent="submitForm" class="p-6">
        <!-- Grid de Partidas -->
        <div class="space-y-4">
          <div 
            v-for="(fixture, id) in currentRound.fixtures" 
            :key="id"
            :class="[
              'bg-white border-2 rounded-xl p-4 transition-all duration-200',
              fixturePostponed(fixture.status) 
                ? 'border-red-200 bg-red-50 opacity-75' 
                : isFixtureBetDisabled(fixture)
                ? 'border-gray-200 bg-gray-50 opacity-75'
                : 'border-gray-200 hover:border-green-300 hover:shadow-md'
            ]">
            
            <!-- Data e Status -->
            <div class="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
              <div class="flex items-center">
                <i class="far fa-calendar-alt text-gray-400 mr-2"></i>
                <span 
                  :class="[
                    'text-sm font-medium',
                    fixturePostponed(fixture.status) ? 'line-through text-red-600' : 'text-gray-600'
                  ]">
                  {{ this.formatDate(fixture.eventDate)}}
                </span>
              </div>
              
              <!-- Badge de Status -->
              <span 
                v-if="fixturePostponed(fixture.status)"
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                <i class="fas fa-exclamation-circle mr-1"></i>
                Partida Adiada
              </span>
            </div>

            <!-- Confronto -->
            <div class="flex items-center justify-between gap-4">
              <!-- Time Casa -->
              <div class="flex-1 flex flex-col sm:flex-row items-center sm:justify-end gap-2 sm:gap-3">
                <span class="font-bold text-gray-900 text-base sm:text-lg text-center sm:text-right order-2 sm:order-1">
                  {{ this.getAbbr(fixture.homeTeam.teamId) }}
                </span>
                <div class="flex-shrink-0 order-1 sm:order-2">
                  <img 
                    :src="fixture.homeTeam.teamLogo" 
                    :alt="fixture.homeTeam.teamName"
                    class="h-12 w-12 object-contain"
                  />
                </div>
              </div>

              <!-- Placar/Inputs -->
              <div class="flex items-center gap-2 px-2 sm:px-4">
                <input 
                  v-model.number="fixture.goalsHomeTeam" 
                  min="0" 
                  :disabled="isFixtureBetDisabled(fixture)" 
                  type="number"
                  :class="[
                    'w-14 h-14 text-center text-2xl font-bold rounded-lg border-2 transition-all duration-200',
                    isFixtureBetDisabled(fixture)
                      ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                      : 'bg-white border-green-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 hover:border-green-400'
                  ]"
                />
                
                <span class="text-2xl font-bold text-gray-400">×</span>
                
                <input 
                  v-model.number="fixture.goalsAwayTeam" 
                  min="0" 
                  :disabled="isFixtureBetDisabled(fixture)" 
                  type="number"
                  :class="[
                    'w-14 h-14 text-center text-2xl font-bold rounded-lg border-2 transition-all duration-200',
                    isFixtureBetDisabled(fixture)
                      ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                      : 'bg-white border-green-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 hover:border-green-400'
                  ]"
                />
              </div>

              <!-- Time Visitante -->
              <div class="flex-1 flex flex-col sm:flex-row items-center sm:justify-start gap-2 sm:gap-3">
                <div class="flex-shrink-0 order-1">
                  <img 
                    :src="fixture.awayTeam.teamLogo" 
                    :alt="fixture.awayTeam.teamName"
                    class="h-12 w-12 object-contain"
                  />
                </div>
                <span class="font-bold text-gray-900 text-base sm:text-lg text-center sm:text-left order-2">
                  {{ this.getAbbr(fixture.awayTeam.teamId) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Mensagem de Ajuda -->
        <div v-if="!isBetDisabled() && currentRound.fixtures" class="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div class="flex items-start">
            <i class="fas fa-lightbulb text-green-600 mt-0.5 mr-3"></i>
            <div class="text-sm text-green-800">
              <p class="font-semibold mb-1">Dica:</p>
              <p>Preencha todos os placares e clique em "Salvar Palpites" para registrar suas apostas. Após salvar, não será possível editar.</p>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import ApiService from '@/services/apiService';
import WhatsAppService from '@/services/whatsappService';
import TeamInfoMap from '@/utils/teamInfoMap';
import { mapGetters } from 'vuex';
import { useToast } from "vue-toastification";
import { useRoute } from 'vue-router'

export default {
  name: 'SaveBettingForm',
  setup() {
      const toast = useToast();
      return { toast }
  },
  props: [
    'userData',
    'roundId'
  ],
  created() {
    this.$watch(
      () => this.$route.params,
      async (toParams, previousParams) => {
        console.log(toParams, previousParams);
        this.getData(toParams.userId, toParams.roundId);
      }
    )
  },
  async mounted() {
    ApiService.getRoundIds().then(rounds => {
      let options = rounds.map(data => {
        return {
          value: data._id,
          label: this.formatTitle(data._id)
        }
      });
      this.roundIds = options;
    });

    const route = useRoute();
    this.userId = route.params.userId;
    if (!this.userId) {
      this.userId = this.userData._id
    }

    await this.getData(this.userId, this.roundId);
    this.hasFixturePostponed = this.currentRound.hasFixturePostponed;
    this.isRoundAlreadyStarted = ApiService.roundInitiaded(this.currentRound.fixtures);
  },
  computed: {
    ...mapGetters('user', {
      userProfile: 'userProfile',
    })
  },
  methods: {
    async getData(userId, roundId) {
      var currentRound = await ApiService.getCurrentRound();
      this.currentRound = currentRound[0];
      if (!roundId) {
        roundId = this.currentRound._id;
      }

      let userBet = await ApiService.getUserBet(userId, roundId);
      if (userBet.length > 0) {
        this.isNewBet = false;
        this.currentRound = userBet[0].betting.rounds[0];
        this.userBetName = userBet[0].name;
      }
    },

    onChangeSelect(event) {
      let roundId = event.target.value;
      return this.$router.push({
          name: "NewBetting",
          params: { userId: this.userId, roundId: roundId }
        })
    },

    async submitForm(e) {
      this.disableSubmit = true;
      e.preventDefault();

      if (this.isBetDisabled() && !this.hasFixturePostponed) {
        return;
      }

      console.log(this.currentRound);
      if (!confirm("Após salvar não será possivel editar os palpites, deseja continuar?")) {
        this.disableSubmit = false;
        return;
      }

      if (this.isNewBet) {
        this.saveBet();
        return;
      }

      if (this.hasPermission("SuperAdmin") || this.hasFixturePostponed) {
        this.updateBet();
        return;
      }

      alert("Você não tem permissão para atualizar o palpite, caso tenha algum erro entre em contato com os administradores");
    },

    saveBet() {
      ApiService.createBetting(this.userId, this.currentRound).then((data) => {
        console.log(data.status);
        this.isNewBet = false;
        this.disableSubmit = false;
        this.toast.success("Palpite salvo com sucesso!");
      });
    },

    updateBet() {
      ApiService.updateBetting(this.userId, this.currentRound).then((data) => {
        console.log(data.status);
        this.isNewBet = false;
        this.disableSubmit = false;
        this.toast.success("Palpite atualizado com sucesso!");
      });
    },

    shareBet() {
      if (this.isNewBet) {
        this.saveBet();
      }
      WhatsAppService.shareBet(this.currentRound, this.userData, this.userBetName);
    },

    getAbbr(teamId) {
      return TeamInfoMap.mapNameToAbbreviation(teamId);
    },

    formatDate(dateString)
    {
      return new Date(dateString).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },

    toggleBet() {
      this.showBet = !this.showBet;
    },

    formatTitle(title) {
      if (title !== undefined) {
        return title.replaceAll("_", " ");
      }
      return title;
    },

    isBetDisabled() {
      if (this.hasPermission("SuperAdmin")) {
        return false;
      }

      return this.isRoundAlreadyStarted || !this.isNewBet;
    },

    isFixtureBetDisabled(fixture) {
      if (this.hasPermission("SuperAdmin")) {
        return false;
      }

      if (this.IsInvalidStatus(fixture.status)) {
        return true;
      }

      return this.isBetDisabled() && !this.hasFixturePostponed;
    },

    IsInvalidStatus(status) {
      return status == "Match Finished" || status == "Match Postponed";
    },

    hasPermission(permission) {
      if (this.userData.length == 0) {
        return false;
      }
      let { rules } = this.userData;
      return rules.filter(data => {
        return data === permission || data === "SuperAdmin"
      }).length > 0;
    },

    fixturePostponed(status) {
      return status == "Match Postponed";
    }
  },
  data(){
    return {
      currentRound: {},
      userBet: {},
      isNewBet: true,
      isRoundAlreadyStarted: false,
      userId: null,
      disableSubmit: false,
      hasFixturePostponed: false,
      roundIds : [],
      userBetName: null,
    }
  },
}
</script>
<style scoped>
/* Animações suaves */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  opacity: 1;
}

input[type="number"]:focus {
  transform: scale(1.05);
}

/* Animação de hover nos cards */
.hover\:border-green-300:hover {
  animation: pulse-border 0.3s ease-in-out;
}

@keyframes pulse-border {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.01);
  }
}

/* Transição suave para estados disabled */
input:disabled {
  transition: all 0.3s ease;
}

/* Melhor visualização dos botões */
button:active:not(:disabled) {
  transform: scale(0.98);
}

/* Responsive adjustments */
@media (max-width: 640px) {
  input[type="number"] {
    width: 3rem;
    height: 3rem;
    font-size: 1.25rem;
  }
}
</style>
